<script setup>
import { ref } from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import '@/assets/styles/swiper-custom.scss';

defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  title: {
    type: String,
    default: () => '',
  },
  id: {
    type: String,
    default: () => '',
  },
  backgroundColor: {
    type: String,
    default: () => '',
  },
});
const isLocked = ref(true);
const onSwiper = (swiper) => {
  isLocked.value = swiper.isLocked;
};

const onLockToggle = (swiper) => {
  isLocked.value = swiper.isLocked;
};
</script>

<template>
  <div
    ref="galleryResultsEl"
    :class="[`gallery-results gallery-results--${backgroundColor}`]"
  >
    <Swiper
      :slides-per-view="1"
      :space-between="16"
      :modules="[Pagination, Navigation]"
      :pagination="{
        type: 'bullets',
        el: `#${id}-pagination`,
        clickable: true,
      }"
      :navigation="{
        prevEl: `#${id}-prev`,
        nextEl: `#${id}-next`,
      }"
      class="gallery-results__swiper"
      data-aos="fade-up"
      data-aos-easing="ease-out-cubic"
      @swiper="onSwiper"
      @lock="onLockToggle"
      @unlock="onLockToggle"
    >
      <template v-for="(item, index) in items" :key="index">
        <SwiperSlide
          v-if="item.before.src && item.after.src"
          class="gallery-results__item"
        >
          <div class="gallery-results__item-wrap">
            <div class="gallery-results__item-top">
              <NuxtImg
                v-if="item?.before?.src"
                :src="item.before.src"
                :alt="item.before.alt"
                :height="item.before.height"
                :width="item.before.width"
                class="gallery-results__item-image gallery-results__item-image--before"
                loading="lazy"
              />
              <NuxtImg
                v-if="item?.after?.src"
                :src="item.after.src"
                :alt="item.after.alt"
                :height="item.after.height"
                :width="item.after.width"
                class="gallery-results__item-image gallery-results__item-image--after"
                loading="lazy"
              />
            </div>
            <div class="gallery-results__item-bottom h4">
              <span>{{ $t('services.before') }}</span>
              <span>{{ $t('services.after') }}</span>
            </div>
          </div>
        </SwiperSlide>
      </template>
    </Swiper>
    <div
      v-show="!isLocked"
      class="gallery-results__bottom"
      data-aos="fade-up"
      data-aos-easing="ease-out-cubic"
    >
      <div
        :id="`${id}-pagination`"
        class="swiper-pagination gallery-results__pagination"
      ></div>
      <div class="swiper-navigation gallery-results__navigation">
        <Button
          :id="`${id}-prev`"
          type="button"
          rounded
          variant="outlined"
          class="gallery-results__navigation-prev button-prev js-button-results-prev"
        >
          <CIcon
            class="button-prev__icon"
            :name="
              backgroundColor === 'black'
                ? 'direction-white'
                : 'direction-black'
            "
            width="16"
            height="16"
          />
        </Button>
        <Button
          :id="`${id}-next`"
          type="button"
          rounded
          variant="outlined"
          class="gallery-results__navigation-next button-next js-button-results-next"
        >
          <CIcon
            class="button-next__icon"
            :name="
              backgroundColor === 'black'
                ? 'direction-white'
                : 'direction-black'
            "
            width="16"
            height="16"
          />
        </Button>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.gallery-results {
  $parent: &;
  &--white {
    #{$parent} {
      &__item {
        background-color: $gray;
      }
    }
  }
  &--black {
    #{$parent} {
      &__item {
        background-color: $surface-container;
      }
    }
  }
  &--gray {
    #{$parent} {
      &__item {
        background-color: $white;
      }
    }
  }
  &__item {
    border-radius: 8px;
    padding: 8px 8px 24px;
    @include media-breakpoint-up(sm) {
      padding: 24px 24px 40px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(padding-bottom, 40);
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(padding-left, 24);
      @include responsive-spacing(padding-right, 24);
    }
    &-wrap {
      display: flex;
      flex-direction: column;
      gap: 16px;
      @include media-breakpoint-up(sm) {
        gap: 26px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 26);
      }
    }
    &-top {
      display: grid;
      gap: 4px;
      grid-template-columns: repeat(2, 1fr);
      @include media-breakpoint-up(sm) {
        gap: 4px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 4);
      }
    }
    &-image {
      width: 100%;
      height: auto;
      &--before {
        border-radius: 4px 0 0 4px;
      }
      &--after {
        border-radius: 4px 4px 0 0;
      }
      @include media-breakpoint-up(xl) {
        &--before {
          @include responsive-spacing(border-radius-top-left, 4);
          @include responsive-spacing(border-radius-bottom-left, 4);
        }
        &--after {
          @include responsive-spacing(border-radius-top-right, 4);
          @include responsive-spacing(border-radius-bottom-right, 4);
        }
      }
    }
    &-bottom {
      color: $on-surface-variant;
      display: grid;
      gap: 4px;
      grid-template-columns: repeat(2, 1fr);
      text-align: center;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 4);
      }
    }
  }
}
</style>
